import type { AggregatedActivityResponse } from 'api/__GENERATED';
import * as R from 'remeda';

// Maps a single AggregatedActivity DTO onto the normalized
// `Data.AggregatedActivity` shape. Most fields on the DTO are nullable — when
// any required field (activity ids or date components) is missing, the row
// can't be placed in the domain and we return `undefined` so the domain
// transformer can filter it out.
//
// `monthNumber` is 1-based on the wire and shifted to 0-based `month` to match
// the rest of our `Time.Day` shape. `id` is generated locally because the
// endpoint returns aggregated rows that have no stable server-side identity.
// `modifiedSpentTime` mirrors `spentTime` initially (the dashboard's toggle
// flow flips it on demand). `movingTotal` defaults to 0 — the legacy
// `decorateDomainWithMovingValue` pass is intentionally not ported yet.
function toAggregatedActivity(
  dto: AggregatedActivityResponse,
): Data.AggregatedActivity | undefined {
  const {
    activityId,
    activityTypeId,
    year,
    monthNumber,
    weekNumber,
    dayOfMonth,
    dayOfWeek,
    spentTime,
  } = dto;

  if (
    !R.isDefined(activityId) ||
    !R.isDefined(activityTypeId) ||
    !R.isDefined(year) ||
    !R.isDefined(monthNumber) ||
    !R.isDefined(weekNumber) ||
    !R.isDefined(dayOfMonth) ||
    !R.isDefined(dayOfWeek)
  ) {
    return undefined;
  }

  const aggregatedActivity: Data.AggregatedActivity = {
    id: R.randomString(12),
    activityId,
    activityTypeId,
    year,
    month: monthNumber - 1,
    week: weekNumber,
    dayOfMonth,
    dayOfWeek,
    spentTime,
    modifiedSpentTime: spentTime,
    movingTotal: 0,
  };

  return aggregatedActivity;
}

export { toAggregatedActivity };
